"use client";

import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";

import FathersDayHero from "./FathersDayHero";

const STORAGE_KEY = "fathersday-banner-collapsed";

// One-shot confetti when the user clicks "Browse Father's Day Cards."
// canvas-confetti loads lazily via dynamic import so it doesn't ship in
// the initial bundle — only fetched on the interaction. Silent failure
// (confetti is non-essential); skipped entirely under reduced-motion.
const fireConfetti = async () => {
  if (typeof window === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  try {
    const confetti = (await import("canvas-confetti")).default;
    confetti({
      particleCount: 120,
      spread: 75,
      origin: { y: 0.65 },
      colors: [
        "#6852c0", // brand purple
        "#F47C7C", // coral
        "#F5B947", // yellow
        "#6BC4D2", // teal
        "#8DACFB", // logo blue
        "#EE4545", // logo red
      ],
      scalar: 1.1,
      ticks: 200,
    });
  } catch {
    // ignored — confetti is purely decorative
  }
};

// Tie rack decorative overlay. The static hero copy lives in
// `FathersDayHero` (server-renderable so crawlers see it); this
// component layers TieRack + animation timing + audio on top after
// hydration.

type TieVariant = "stripes" | "dots" | "pinstripe";

const TieIcon = ({ variant }: { variant: TieVariant }) => {
  const bodyColor =
    variant === "stripes"
      ? "#F47C7C"
      : variant === "dots"
        ? "#6BC4D2"
        : "#F5B947";
  const knotColor = variant === "stripes" ? "#F5B947" : "#F47C7C";

  return (
    <svg
      viewBox="0 0 40 88"
      fill="none"
      className="h-14 w-6 md:h-16 md:w-7 lg:h-20 lg:w-8"
      aria-hidden="true"
    >
      <path
        d="M20 0 L30 5 L33 14 L28 22 L12 22 L7 14 L10 5 Z"
        fill={knotColor}
        stroke="#FFFFFF"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M12 22 L28 22 L36 70 L20 86 L4 70 Z"
        fill={bodyColor}
        stroke="#FFFFFF"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {variant === "stripes" && (
        <>
          <line x1="9" y1="36" x2="31" y2="44" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="6" y1="50" x2="34" y2="58" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="5" y1="64" x2="35" y2="72" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
        </>
      )}
      {variant === "dots" && (
        <>
          <circle cx="14" cy="36" r="2" fill="#FFFFFF" />
          <circle cx="26" cy="42" r="2" fill="#FFFFFF" />
          <circle cx="14" cy="50" r="2" fill="#FFFFFF" />
          <circle cx="26" cy="56" r="2" fill="#FFFFFF" />
          <circle cx="14" cy="64" r="2" fill="#FFFFFF" />
          <circle cx="26" cy="70" r="2" fill="#FFFFFF" />
        </>
      )}
      {variant === "pinstripe" && (
        <line
          x1="20"
          y1="28"
          x2="20"
          y2="78"
          stroke="#FFFFFF"
          strokeWidth="1.5"
          strokeDasharray="3 4"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
};

const GiftBoxBody = () => (
  <svg
    viewBox="0 0 60 40"
    fill="none"
    className="block h-10 w-16 md:h-12 md:w-20 lg:h-14 lg:w-24"
    aria-hidden="true"
  >
    <rect x="3" y="10" width="54" height="28" rx="2" fill="#8DACFB" stroke="#FFFFFF" strokeWidth="2" />
    <rect x="16" y="10" width="9" height="28" fill="#EE4545" />
  </svg>
);

const GiftBoxLid = () => (
  <svg
    viewBox="0 0 60 22"
    fill="none"
    className="block h-5 w-16 md:h-6 md:w-20 lg:h-7 lg:w-24"
    aria-hidden="true"
  >
    <ellipse cx="14" cy="6" rx="7" ry="4.5" fill="#EE4545" stroke="#FFFFFF" strokeWidth="0.8" />
    <ellipse cx="26" cy="6" rx="7" ry="4.5" fill="#EE4545" stroke="#FFFFFF" strokeWidth="0.8" />
    <rect x="17" y="2" width="6" height="9" rx="0.5" fill="#CD5959" stroke="#FFFFFF" strokeWidth="0.6" />
    <rect x="2" y="11" width="56" height="9" rx="2" fill="#B5C9FF" stroke="#FFFFFF" strokeWidth="2" />
    <rect x="16" y="11" width="9" height="9" fill="#EE4545" />
  </svg>
);

const ClosingSparkles = () => (
  <svg
    viewBox="0 0 80 80"
    fill="none"
    className="h-full w-full"
    aria-hidden="true"
  >
    <path d="M40 14 L43 24 L53 27 L43 30 L40 40 L37 30 L27 27 L37 24 Z" fill="#F5B947" />
    <path d="M18 30 L21 33 L18 36 L15 33 Z" fill="#8DACFB" />
    <path d="M62 32 L65 35 L62 38 L59 35 Z" fill="#F47C7C" />
    <path d="M22 60 L24 62 L22 64 L20 62 Z" fill="#F47C7C" />
    <path d="M58 58 L60 60 L58 62 L56 60 Z" fill="#8DACFB" />
    <circle cx="10" cy="40" r="2" fill="#F5B947" />
    <circle cx="70" cy="42" r="2" fill="#F5B947" />
    <circle cx="40" cy="68" r="2" fill="#F47C7C" />
    <circle cx="48" cy="10" r="1.5" fill="#8DACFB" />
    <circle cx="32" cy="12" r="1.5" fill="#F47C7C" />
  </svg>
);

const CatchBox = () => (
  <div className="relative opacity-0 motion-safe:animate-gift-box-show">
    <GiftBoxBody />
    <div className="absolute -top-2.5 left-0 w-full origin-bottom opacity-0 motion-safe:animate-gift-lid-close md:-top-3 lg:-top-3.5">
      <GiftBoxLid />
    </div>
    <div className="pointer-events-none absolute -inset-6 opacity-0 motion-safe:animate-gift-sparkle-pop">
      <ClosingSparkles />
    </div>
  </div>
);

// Speaker-with-waves icon. White speaker box, yellow sound waves so
// it pops on the bg-wg-purple hero and reads as "tap to hear it."
const SoundOnIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-7 w-7 md:h-8 md:w-8"
    aria-hidden="true"
  >
    <path
      d="M4 12 L4 20 L9 20 L15 26 L15 6 L9 12 Z"
      fill="#FFFFFF"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M18 12 Q21 16 18 20"
      stroke="#F5B947"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M22 9 Q27 16 22 23"
      stroke="#F5B947"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

// Opt-in audio button. Sits to the left of the TieRack and fades out
// just before the falling tie starts its drop. Clicking it (within the
// fade window) enables the tie-fall sound; ignoring it keeps the
// animation silent. Once faded, the button becomes non-interactive.
interface SoundEnableButtonProps {
  onClick: () => void;
  faded: boolean;
  enabled: boolean;
}

const SoundEnableButton = ({
  onClick,
  faded,
  enabled,
}: SoundEnableButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={
      enabled ? "Tie-fall sound enabled" : "Tap to enable tie-fall sound"
    }
    aria-pressed={enabled}
    disabled={faded || enabled}
    className={`pointer-events-auto flex flex-col items-center gap-1 rounded-full bg-white/15 px-2 py-2 text-[10px] font-medium uppercase tracking-wide text-white/90 backdrop-blur-sm transition-opacity duration-700 ease-out hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 md:text-xs ${
      faded ? "pointer-events-none opacity-0" : "opacity-100"
    }`}
  >
    <SoundOnIcon />
    <span aria-hidden="true">{enabled ? "On" : "Sound"}</span>
  </button>
);

const TieRack = () => (
  <div className="relative h-80 w-28 md:w-36 lg:w-44">
    <div className="absolute left-0 right-0 top-2 h-1.5 rounded-full bg-white/85" />
    <div className="absolute left-0 top-0.5 h-4 w-1.5 rounded-full bg-white/85" />
    <div className="absolute right-0 top-0.5 h-4 w-1.5 rounded-full bg-white/85" />

    <div
      className="absolute left-[10%] top-1.5 origin-top motion-safe:animate-gentle-sway"
      style={{ animationDelay: "0s" }}
    >
      <TieIcon variant="stripes" />
    </div>
    <div className="absolute left-1/2 top-1.5 -translate-x-1/2">
      <div className="motion-safe:animate-tie-fall">
        <div
          className="origin-top motion-safe:animate-gentle-sway"
          style={{ animationDelay: "-1.6s" }}
        >
          <TieIcon variant="dots" />
        </div>
      </div>
    </div>
    <div
      className="absolute right-[10%] top-1.5 origin-top motion-safe:animate-gentle-sway"
      style={{ animationDelay: "-3.2s" }}
    >
      <TieIcon variant="pinstripe" />
    </div>

    <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
      <CatchBox />
    </div>
  </div>
);

interface FathersDayNarrativeBannerProps {
  onStartCreating?: () => void;
}

// Audio playback timing constants (all in ms relative to expand/mount):
//
//   TIE_FALL_AUDIO_DELAY_MS — when the audio should start so its
//     ramp-up covers the falling tie and the impact lands at the catch.
//   SOUND_ICON_FADE_DELAY_MS — when the "Sound" opt-in icon begins to
//     fade out. Must complete before the tie starts falling (3s delay
//     in `animate-tie-fall`).
const TIE_FALL_AUDIO_DELAY_MS = 5700;
const SOUND_ICON_FADE_DELAY_MS = 2200;
const TIE_FALL_TARGET_VOLUME = 0.4;

export default function FathersDayNarrativeBanner({
  onStartCreating,
}: FathersDayNarrativeBannerProps) {
  const searchParams = useSearchParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // Audio is OFF by default — the user must opt in via the speaker
  // icon. This avoids surprising audio on a marketing page and means
  // we don't need autoplay-policy gymnastics: the click itself is the
  // user gesture that unlocks playback.
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [soundIconFaded, setSoundIconFaded] = useState(false);
  // Wall-clock time of the last (re-)mount of the expanded banner.
  // We schedule the audio relative to this so its impact aligns with
  // the visual catch even when the user clicks the icon partway
  // through the animation.
  const mountTimeRef = useRef<number>(0);
  // The Audio element created+unlocked synchronously inside the
  // SoundEnableButton click handler. iOS Safari (and some Firefox
  // configs) require play() to be called within the user-gesture
  // callstack to unlock playback; a play() invoked later from a
  // useEffect is still treated as autoplay and gets blocked. We
  // construct + prime the element here so the scheduled effect below
  // can simply call .play() on the already-unlocked element.
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedValue = localStorage.getItem(STORAGE_KEY);
    const hasCategory = searchParams.get("category");
    const hasSearch = searchParams.get("search");

    if (hasCategory || hasSearch || storedValue === "true") {
      setIsCollapsed(true);
    }

    setIsInitialized(true);
  }, [searchParams]);

  useEffect(() => {
    if (!isInitialized || typeof window === "undefined") return;

    const hasCategory = searchParams.get("category");
    const hasSearch = searchParams.get("search");

    if (hasCategory || hasSearch) {
      setIsCollapsed(true);
      localStorage.setItem(STORAGE_KEY, "true");
    }
  }, [searchParams, isInitialized]);

  // Track the wall-clock mount of the expanded banner + fade the
  // "Sound" opt-in icon before the tie starts to fall (3s). Reset
  // audioEnabled when the banner re-expands so the icon reappears
  // and the user can opt in again next time.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isCollapsed) return;

    mountTimeRef.current = Date.now();
    setSoundIconFaded(false);
    setAudioEnabled(false);

    // Drop any previously-unlocked Audio element so the next opt-in
    // primes a fresh one inside its own user gesture.
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }

    const fadeTimer = window.setTimeout(() => {
      setSoundIconFaded(true);
    }, SOUND_ICON_FADE_DELAY_MS);

    return () => {
      window.clearTimeout(fadeTimer);
    };
  }, [isCollapsed]);

  // Tie-fall sound. The clip's ramp-up *is* the sound of the tie
  // falling, so playback starts TIE_FALL_AUDIO_DELAY_MS after expand
  // — the ramp narrates the descent and the impact lands at the
  // visual catch (~6.8s). The lid-close moment is visual-only. If
  // the audio file is ever swapped, retune the constant against the
  // new clip's impact offset (see [[tie-fall-audio-timing]] note).
  //
  // The Audio element itself was already constructed + primed
  // synchronously inside handleEnableAudio (the speaker-button click
  // handler). That's intentional: iOS Safari requires play() to be
  // called within the same callstack as the user gesture to unlock
  // playback. Calling play() here from an effect would still be
  // treated as autoplay and blocked. This effect's only job is to
  // schedule the actual ramp-aligned playback on the already-unlocked
  // element.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isCollapsed) return;
    if (!audioEnabled) return;
    const audio = audioRef.current;
    if (!audio) return;

    // Schedule the actual playback relative to mount, so the impact
    // lands at the visual catch regardless of how late the user
    // clicked the speaker icon.
    const elapsedSinceMount = Date.now() - mountTimeRef.current;
    const delayUntilPlay = Math.max(
      0,
      TIE_FALL_AUDIO_DELAY_MS - elapsedSinceMount,
    );

    const timer = window.setTimeout(() => {
      const playPromise = audio.play();
      if (playPromise) {
        playPromise.catch((err: unknown) => {
          console.warn("[tie-fall] scheduled play() blocked", err);
        });
      }
    }, delayUntilPlay);

    return () => {
      window.clearTimeout(timer);
      audio.pause();
    };
  }, [isCollapsed, audioEnabled]);

  // Speaker-icon click. Runs synchronously inside the user gesture so
  // the prime play() satisfies iOS Safari + strict Firefox autoplay
  // policies — they require the play() invocation to be in the same
  // callstack as the click, not deferred to a useEffect. We construct
  // the Audio, set volume=0, call play(), then on resolve restore the
  // target volume and pause. The scheduled-playback effect above then
  // just calls .play() on the already-unlocked element.
  //
  // Skipped under prefers-reduced-motion (no audio created at all).
  const handleEnableAudio = () => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    if (!audioRef.current) {
      const audio = new Audio("/sounds/tie-fall.mp3");
      audio.preload = "auto";
      audio.volume = 0;

      // Modern browsers return a Promise from play(); jsdom and very
      // old browsers return undefined, so we guard the chain.
      const primePromise = audio.play();
      if (primePromise) {
        primePromise
          .then(() => {
            audio.pause();
            audio.currentTime = 0;
            audio.volume = TIE_FALL_TARGET_VOLUME;
          })
          .catch((err: unknown) => {
            audio.volume = TIE_FALL_TARGET_VOLUME;
            console.warn("[tie-fall] audio prime failed", err);
          });
      } else {
        audio.volume = TIE_FALL_TARGET_VOLUME;
      }

      audioRef.current = audio;
    }

    setAudioEnabled(true);
  };

  const handleStartCreating = () => {
    setIsCollapsed(true);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "true");
    }
    void fireConfetti();
    onStartCreating?.();
  };

  const handleLearnMore = () => {
    setIsCollapsed(false);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "false");
    }
  };

  const handleFaqAnchorClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (typeof window === "undefined") return;
    const target = document.getElementById("faq");
    if (!target) return;
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  if (isCollapsed) {
    return (
      <section className="bg-wg-purple text-white">
        <div className="mx-auto flex max-w-4xl flex-col items-start justify-between gap-3 px-6 py-4 sm:flex-row sm:items-center">
          <h1 className="text-xl font-bold md:text-2xl">
            Father&apos;s Day eCards — Sunday, June 21, 2026
          </h1>
          <button
            onClick={handleLearnMore}
            aria-expanded="false"
            aria-label="Learn more about Father's Day eCards"
            className="flex items-center gap-1 text-sm font-medium text-white/90 transition-colors hover:text-white"
          >
            Learn more
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      </section>
    );
  }

  return (
    <div className="relative">
      {/* Tie rack + sound opt-in icon live in the top-right corner of
          the hero (directly below the navbar's Get Started Free
          button). The sound button sits to the left of the rack and
          fades out before the falling tie begins its drop. The outer
          container intentionally does NOT have pointer-events-none —
          the rack itself does, but the button stays clickable. */}
      <div className="absolute right-2 top-0 z-10 flex items-start gap-2 md:right-8 lg:right-16">
        <div className="pt-2">
          <SoundEnableButton
            onClick={handleEnableAudio}
            faded={soundIconFaded}
            enabled={audioEnabled}
          />
        </div>
        <div aria-hidden="true" className="pointer-events-none">
          <TieRack />
        </div>
      </div>

      <FathersDayHero
        onBrowseClick={handleStartCreating}
        onFaqClick={handleFaqAnchorClick}
      />
    </div>
  );
}
